import { GridApi } from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { FilterModel } from '@helios/filter-modal'
import {
  Account,
  AccountsRequestContext,
  FILTER_ROW_COUNT,
} from '@helios/generic-components'
import { BulkActivateNewerExternalDataComponent } from '@helios/panels/external-file-manager'
import {
  convertFilters,
  genericHeliosRequest,
  HeliosServerSideRequest,
} from '@helios/server-side-util'
import { ModalPositionService } from '@helios/user-preferences'
import { getYesterday } from '@helios/utils'
import {
  NgbDropdown,
  NgbModal,
  NgbModalOptions,
} from '@ng-bootstrap/ng-bootstrap'
import { formatDate, ISODateFormat } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { finalize, map, take } from 'rxjs/operators'
import {
  AccountListService,
  AdvancedFilterContext,
} from '../../services/account-list.service'
import { BulkRunFilteredAccountsComponent } from '../bulk-run-filtered-accounts-modals/bulk-run-filtered-accounts/bulk-run-filtered-accounts.component'

export interface FilterAction {
  label: string
  action: () => void
  isDisabled?: boolean
  isLoading?: boolean
}

@Component({
  selector: 'hs-account-list-filter-actions',
  templateUrl: './account-list-filter-actions.component.html',
  styleUrls: ['./account-list-filter-actions.component.scss'],
})
export class AccountListFilterActionsComponent {
  accountIds: number[] = []
  rowCount = FILTER_ROW_COUNT
  showLoader = false
  filterActions = this.enableFilter()
  @Input() gridApi?: GridApi
  @Input() filterCount = 0
  @Input() advancedFilterContextValue?: AdvancedFilterContext

  filteredRowCount = 0

  @ViewChild(NgbDropdown) dropdown: NgbDropdown | undefined

  @Output() exportToExcel = new EventEmitter<void>()

  advancedFilterContext: AdvancedFilterContext = {
    advancedFilters: {},
    reconDate: formatDate(getYesterday(), ISODateFormat) ?? '',
  }

  onToggleDropdown() {
    if (this.dropdown?.isOpen()) {
      this.getAccountListCountForFilter()
    }
  }

  getAccountListCountForFilter() {
    this.showLoader = true
    const request: HeliosServerSideRequest<Account, AccountsRequestContext> = {
      ...genericHeliosRequest,
      filter: convertFilters(this.gridApi?.getFilterModel() as FilterModel),
      context: {
        filterContext: {
          ...this.advancedFilterContextValue,
          reconDate:
            this.advancedFilterContextValue?.reconDate ??
            this.advancedFilterContext.reconDate,
        },
      } as AccountsRequestContext,
    }

    this.accountListService
      .getAccountListCountForFilter(request)
      .pipe(take(1))
      .subscribe(response => {
        this.filteredRowCount = response
        this.showLoader = false
        this.filterActions = this.enableFilter()
      })
  }

  filterActionTooltip: string | undefined

  isLoadingData(index: number) {
    return this.filterActions[index].isLoading
  }
  enableFilter(): FilterAction[] {
    //Do not change the order of the filter actions. Index has been used to identify the filter actions
    return [
      {
        label: 'Run Accounts',
        action: () => this.handleFilterActions('RunAccounts'),
        isDisabled: this.filteredRowCount > FILTER_ROW_COUNT,
      },
      {
        label: 'Activate External Data',
        action: () => this.handleFilterActions('ActivateExternalData'),
        isDisabled: this.filteredRowCount > FILTER_ROW_COUNT,
      },
      {
        label: 'Find Breaks and Variances',
        action: () => this.handleFilterActions('FindBreaksAndVariances'),
        isDisabled: true,
      },
      {
        label: 'Export to Excel',
        action: () => this.handleFilterActions('ExportToExcel'),
      },
      {
        label: 'Copy Account IDs',
        action: () => this.handleFilterActions('CopyAccountIds'),
        isDisabled: this.filteredRowCount > FILTER_ROW_COUNT,
      },
    ]
  }

  constructor(
    readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private readonly accountListService: AccountListService,
    readonly modalPositionService: ModalPositionService,
  ) {}

  /** @internal */
  // updateTooltipContent() {
  //   if (
  //     this.filteredRowCount > FILTER_ROW_COUNT ||
  //     this.filteredRowCount === 0
  //   ) {
  //     return
  //   }

  //   this.filterActionTooltip = `${this.filteredRowCount} ${
  //     this.filteredRowCount < 2 ? 'account' : 'accounts'
  //   } in filter`
  // }

  /** @internal */
  async copyFilteredAccountIds() {
    const action = this.filterActions[4]
    action.isLoading = true
    this.accountIds = await this.loadAccountIds(action)
    navigator.clipboard.writeText(this.accountIds.join(','))
    this.toastService.success('Copied Account Ids to clipboard')
  }

  async toggleBulkRunAccountsModal() {
    const action = this.filterActions[0]
    action.isLoading = true
    this.accountIds = await this.loadAccountIds(action)

    const compKey = 'bulk-run-filtered-accounts'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(BulkRunFilteredAccountsComponent, {
      size: 'md',
      modalDialogClass: 'modal-mdl',
      windowClass: `modal-window-click-through ${uniqueId}`,
      backdrop: false,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.filteredAccountIds = this.accountIds.join(',')
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  async toggleBulkActivateExternalDataModal() {
    const action = this.filterActions[1]
    action.isLoading = true
    this.accountIds = await this.loadAccountIds(action)

    const compKey = 'bulk-activate-newer-external-data'
    const uniqueId = compKey + '-' + Date.now()
    const modelRef = this.modalService.open(
      BulkActivateNewerExternalDataComponent,
      {
        size: 'md',
        windowClass: `modal-window-click-through ${uniqueId}`,
        backdrop: false,
        keyboard: false,
        beforeDismiss: () => {
          return this.modalPositionService.handleModalPositionSaveOnDismiss(
            uniqueId,
            compKey,
          )
        },
      } as NgbModalOptions,
    )
    modelRef.componentInstance.selectedAccountIds = this.accountIds.join(',')
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  async loadAccountIds(action: FilterAction) {
    return await this.accountListService
      .getAccountIdsForFilterAction(
        this.accountRequest(),
        this.filteredRowCount,
      )
      .pipe(
        take(1),
        map(response => {
          const accountData = response.map(acc => acc.data).flat()
          return accountData.map(account => account.accountId)
        }),
        finalize(() => {
          action.isLoading = false
          this.dropdown?.close()
        }),
      )
      .toPromise()
  }
  accountRequest() {
    const request: HeliosServerSideRequest<Account, AccountsRequestContext> = {
      ...genericHeliosRequest,
      fields: ['accountId'],
      filter: convertFilters(this.gridApi?.getFilterModel() as FilterModel),
      context: {
        filterContext: {
          ...this.advancedFilterContextValue,
          reconDate:
            this.advancedFilterContextValue?.reconDate ??
            this.advancedFilterContext.reconDate,
        },
      } as AccountsRequestContext,
    }
    return request
  }

  /** @internal */
  handleFilterActions(label: string) {
    switch (label) {
      case 'RunAccounts':
        this.toggleBulkRunAccountsModal()
        break
      case 'ActivateExternalData':
        this.toggleBulkActivateExternalDataModal()
        break
      // Placeholder for the Find B&V Action button
      // case 'FindBreaksAndVariances':
      //   break
      case 'ExportToExcel':
        this.exportToExcel.emit()
        this.dropdown?.close()
        break
      case 'CopyAccountIds':
        this.copyFilteredAccountIds()
        break
      default:
        break
    }
  }
}
